import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserInfoModal from "../../other/UserInfoModal/UserInfoModal";
import ProfilePhotoControl from "./ProfilePhotoControl";
import MySubscriptionSection from "./MySubscriptionSection";
import SubscribeBanner from "./SubscribeBanner";
import CaptainStoryStrip from "./CaptainStoryStrip";
import { formatHostsSentence } from "../../functions/hosts";
import { executeQuery } from "../../../graphql/client";
import "./userdashboard.css";

const UserDashboard = ({
  userInfo: propUserInfo,
  cognitoToken: propCognitoToken,
  userRole: propUserRole,
  onLogout,
  onUserInfoUpdate,
}) => {
  const navigate = useNavigate();

  // State management
  const [localUserInfo, setLocalUserInfo] = useState(null);
  const [localToken, setLocalToken] = useState(null);
  const [localUserRole, setLocalUserRole] = useState(null);
  const [activeTab, setActiveTab] = useState("events");
  const [socialEvents, setSocialEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [showSubscribePrompt, setShowSubscribePrompt] = useState(false);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [currentFeatureType, setCurrentFeatureType] = useState(null);
  const [currentEventId, setCurrentEventId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chats, setChats] = useState([]);

  // Read from session storage if props are not provided
  useEffect(() => {
    // If props are provided, use them
    if (propUserInfo && propCognitoToken && propUserRole) {
      setLocalUserInfo(propUserInfo);
      setLocalToken(propCognitoToken);
      setLocalUserRole(propUserRole);
      return;
    }

    // Otherwise, try to get from session storage
    const storedToken = sessionStorage.getItem("cognitoIdToken");
    if (!storedToken) {
      // Redirect to login if no token
      navigate("/login");
      return;
    }

    // Set token
    setLocalToken(storedToken);

    try {
      // Parse token to get basic user info
      const payload = JSON.parse(atob(storedToken.split(".")[1]));

      // Get stored profile info if available
      const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
      if (storedProfileInfo) {
        const profileInfo = JSON.parse(storedProfileInfo);
        setLocalUserInfo({
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
          phone: profileInfo.phone || "",
          sex: profileInfo.sex || "",
          shirtSize: profileInfo.shirtSize || "",
          interestedEvents:
            profileInfo.interestedEvents ||
            profileInfo.reInterestedInEvents ||
            "",
          referredBy: profileInfo.referredBy || "",
        });
      } else {
        setLocalUserInfo({
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
        });
      }

      // Set user role
      setLocalUserRole(
        payload["cognito:groups"]?.includes("Admins")
          ? "admin"
          : payload["cognito:groups"]?.includes("Members")
          ? "member"
          : "user"
      );
    } catch (e) {
      console.error("Error parsing JWT token:", e);
      navigate("/login");
    }
  }, [propUserInfo, propCognitoToken, propUserRole, navigate]);

  // Add useEffect to load user info from storage on component mount
  useEffect(() => {
    const loadedInfo = loadUserProfileInfoFromStorage();
    console.log("Initial load of user info:", loadedInfo);

    // If we don't have stored info yet, check if we can load from props
    if (!loadedInfo && propUserInfo) {
      setLocalUserInfo(propUserInfo);
    }
  }, [propUserInfo]);

  // Use proper values from either props or local state
  const userInfo = propUserInfo || localUserInfo;
  const cognitoToken = propCognitoToken || localToken;
  const userRole = propUserRole || localUserRole;

  // Handle logout if no callback provided
  const handleLocalLogout = () => {
    // Clear tokens
    sessionStorage.removeItem("cognitoIdToken");
    sessionStorage.removeItem("userProfileInfo");

    // Redirect to login
    navigate("/login");
  };

  // The actual logout function to use
  const logoutFn = onLogout || handleLocalLogout;

  // Load social events and chats when dashboard is loaded
  useEffect(() => {
    if (cognitoToken && userInfo) {
      if (activeTab === "events") {
        loadSocialEvents();
      } else if (
        activeTab === "chats" &&
        (userRole === "member" ||
          userRole === "admin" ||
          userInfo?.sex === "female")
      ) {
        loadChats();
      }
    }
  }, [activeTab, userRole, userInfo, cognitoToken]);

  // Load social events from GraphQL API
  const loadSocialEvents = async () => {
    setLoading(true);
    setError("");

    try {
      const eventsQuery = `
        query GetEvents($days: Int) {
          events(days: $days) {
            id
            title
            date
            time
            location
            privateLocation
            description
            generalpublic
            imgKey
            hosts
          }
        }
      `;

      const result = await executeQuery(eventsQuery, { days: 14 });

      // Debug: log the raw events data from API
      console.log("Raw events from GraphQL:", result?.events);

      if (result?.events) {
        // The dates are already formatted, but we want to reformat them to "Sun May 4" style
        const formattedEvents = result.events.map((event) => {
          console.log(
            "Processing event:",
            event.id,
            "Original date value:",
            event.date
          );

          const updatedEvent = { ...event };

          if (updatedEvent.date) {
            try {
              // Parse the existing date (e.g., "May 4, 2025")
              const dateObj = new Date(updatedEvent.date);

              // Check if this date is valid
              if (!isNaN(dateObj.getTime())) {
                // Format to "Sun May 4" (weekday, month, day)
                updatedEvent.date = dateObj.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: undefined, // Exclude year
                });
                console.log("  Reformatted date:", updatedEvent.date);
              }
            } catch (e) {
              console.error("Error reformatting date:", e);

              // If reformatting fails, try to extract from ID as fallback
              if (updatedEvent.id && typeof updatedEvent.id === "string") {
                try {
                  const dateParts = updatedEvent.id.split("-");
                  if (dateParts.length >= 3) {
                    const [year, month, day] = dateParts;
                    // Only proceed if these look like date parts (numbers)
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                      const date = new Date(
                        parseInt(year, 10),
                        parseInt(month, 10) - 1,
                        parseInt(day, 10)
                      );

                      const formattedDate = date.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: undefined, // Exclude year
                      });
                      console.log("  Created date from ID:", formattedDate);
                      updatedEvent.date = formattedDate;
                    }
                  }
                } catch (e) {
                  console.error("Error creating date from ID:", e);
                }
              }
            }
          } else if (updatedEvent.id && typeof updatedEvent.id === "string") {
            // If no date field, try to extract from ID
            try {
              const dateParts = updatedEvent.id.split("-");
              if (dateParts.length >= 3) {
                const [year, month, day] = dateParts;
                // Only proceed if these look like date parts (numbers)
                if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                  const date = new Date(
                    parseInt(year, 10),
                    parseInt(month, 10) - 1,
                    parseInt(day, 10)
                  );

                  const formattedDate = date.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: undefined, // Exclude year
                  });
                  console.log("  Created date from ID:", formattedDate);
                  updatedEvent.date = formattedDate;
                }
              }
            } catch (e) {
              console.error("Error creating date from ID:", e);
            }
          }

          return updatedEvent;
        });

        console.log("Events ready for display:", formattedEvents);
        setSocialEvents(formattedEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Load chats from GraphQL API
  const loadChats = async () => {
    setLoading(true);
    setError("");

    try {
      const chatsQuery = `
        query GetChats {
          chats {
            id
            name
            description
            imageUrl
            createdAt
            updatedAt
            memberCount
            messageCount
          }
        }
      `;

      const result = await executeQuery(chatsQuery);

      if (result?.chats) {
        setChats(result.chats);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chats. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Load user profile information from storage
  const loadUserProfileInfoFromStorage = () => {
    try {
      const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
      if (storedProfileInfo) {
        const profileInfo = JSON.parse(storedProfileInfo);
        console.log("Loaded profile info from storage:", profileInfo);

        // Normalize fields by checking for various possible field names
        const normalizedInfo = {
          name:
            profileInfo.name ||
            profileInfo.username ||
            profileInfo.firstName ||
            "",
          email: profileInfo.email || profileInfo.emailAddress || "",
          phone:
            profileInfo.phone ||
            profileInfo.phoneNumber ||
            profileInfo.userPhone ||
            "",
          shirtSize:
            profileInfo.shirtSize ||
            profileInfo.shirt_size ||
            profileInfo.tshirt ||
            "",
          age: profileInfo.age || profileInfo.userAge || "",
          sex: profileInfo.sex || profileInfo.gender || "",
          interestedEvents:
            profileInfo.interestedEvents ||
            profileInfo.reInterestedInEvents ||
            [],
          // Add any other fields needed
        };

        setLocalUserInfo(normalizedInfo);
        return normalizedInfo;
      }
    } catch (e) {
      console.error("Error loading user profile from storage:", e);
    }
    return null;
  };

  // Handle user info update locally if no callback provided
  const handleLocalUserInfoUpdate = (updatedInfo) => {
    // Update local user info
    setLocalUserInfo((prev) => ({
      ...prev,
      ...updatedInfo,
    }));

    // Save to session storage
    try {
      const storedToken = sessionStorage.getItem("cognitoIdToken");
      if (storedToken) {
        // Get existing stored profile info if available
        let existingProfile = {};
        const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
        if (storedProfileInfo) {
          try {
            existingProfile = JSON.parse(storedProfileInfo);
          } catch (e) {
            console.error("Error parsing existing profile info:", e);
          }
        }

        // Merge with updated info
        sessionStorage.setItem(
          "userProfileInfo",
          JSON.stringify({
            ...existingProfile,
            ...updatedInfo,
          })
        );

        // Log success for debugging
        console.log(
          "Updated user profile information saved to session storage:",
          {
            ...existingProfile,
            ...updatedInfo,
          }
        );
      }
    } catch (e) {
      console.error("Error saving user profile info to session storage:", e);
    }
  };

  // Use the provided callback or fall back to local implementation
  const userInfoUpdateFn = onUserInfoUpdate || handleLocalUserInfoUpdate;

  // Handle toggling event card expansion
  const toggleEventExpansion = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  // Generate random attendance number (8-14) for now
  const getRandomAttendance = () => {
    return Math.floor(Math.random() * 7) + 8;
  };

  // Handle showing the appropriate modal based on user information
  const handleShowAttendees = (e, eventId) => {
    e.stopPropagation(); // Prevent collapsing the card when button is clicked

    // If user is already a member or admin, they can see everything
    if (userRole === "member" || userRole === "admin") {
      // Future: implement showing attendee list directly
      console.log("Show attendees list for event:", eventId);
      return;
    }

    // Check if we have phone and sex info in userInfo
    if (!userInfo.phone || !userInfo.sex) {
      setCurrentEventId(eventId);
      setCurrentFeatureType("attendees");
      setShowUserInfoModal(true);
    } else if (userInfo.sex === "female") {
      // Future: implement showing attendee list for females
      console.log("Show attendees list for female user, event:", eventId);
    } else {
      // For males, show subscription prompt
      setShowSubscribePrompt(true);
    }
  };

  // Handle tab change with checking for user info
  const handleTabChange = (tab) => {
    if (tab === "chats" && !(userRole === "member" || userRole === "admin")) {
      // Check if we have phone and sex info in userInfo
      if (!userInfo.phone || !userInfo.sex) {
        setCurrentFeatureType("chats");
        setShowUserInfoModal(true);
      } else if (userInfo.sex === "female") {
        // Just switch tabs for females since they get free access
        setActiveTab(tab);
        loadChats();
      } else {
        // For males, show subscription prompt
        setShowSubscribePrompt(true);
      }
    } else {
      setActiveTab(tab);
    }
  };

  // Handle user info submission
  const handleUserInfoSubmit = async (userData) => {
    // Update user info in the parent component via callback
    if (typeof userInfoUpdateFn === "function") {
      userInfoUpdateFn(userData);
    }

    // Close the modal
    setShowUserInfoModal(false);

    // Handle the requested feature based on sex
    if (userData.sex === "female") {
      if (currentFeatureType === "chats") {
        setActiveTab("chats");
        loadChats();
      } else if (currentFeatureType === "attendees") {
        // Future: implement showing attendee list for females
        console.log(
          "Show attendees list for female user, event:",
          currentEventId
        );
      }
    } else if (
      currentFeatureType === "chats" ||
      currentFeatureType === "attendees"
    ) {
      // For males coming from a gated feature, show the subscription
      // prompt. The "subscribe" and "profile" flows skip it — the
      // Events-tab banner / Account section update in place instead.
      setShowSubscribePrompt(true);
    }
  };

  // Close subscription prompt
  const closeSubscribePrompt = () => {
    setShowSubscribePrompt(false);
  };

  // Handle subscription button click
  const handleSubscribe = () => {
    // Navigate to subscription page
    window.location.href = "/subscribe";
  };

  // Handle navigating to chat detail
  const handleChatSelect = (chatId) => {
    // For future implementation
    console.log("Selected chat:", chatId);
    // navigate(`/chats/${chatId}`);
  };

  // Format date to display nicely
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Show loading state while fetching user info
  if (!userInfo || !cognitoToken) {
    return <div className="loading-container">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        {userInfo && (
          <span className="user-email">
            {userInfo.email} ({userRole})
          </span>
        )}
      </header>

      <div className="dashboard-content">
        {loading && <div className="loading-spinner">Loading...</div>}

        {error && <div className="error-message">{error}</div>}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div className="tab-content">
            <SubscribeBanner
              userInfo={userInfo}
              onNeedInfo={() => {
                setCurrentFeatureType("subscribe");
                setShowUserInfoModal(true);
              }}
            />
            <CaptainStoryStrip />
            {socialEvents.length === 0 && !loading ? (
              <div className="no-data">No upcoming social events found</div>
            ) : (
              <>
                <div className="events-list">
                  {socialEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`event-card ${
                        !event.generalpublic ? "private" : ""
                      } ${expandedEvents[event.id] ? "expanded" : ""}`}
                      onClick={() => toggleEventExpansion(event.id)}
                    >
                      <div className="event-header">
                        <div className="event-title">
                          <h3>{event.title}</h3>
                          <div className="event-meta">
                            <span className="event-date">{event.date}</span>
                            <span className="event-time">{event.time}</span>
                            <span className="event-attendance">
                              <span className="attendance-icon">👥</span>
                              {getRandomAttendance()}
                            </span>
                          </div>
                          {(() => {
                            const hostsLine = formatHostsSentence(event.hosts);
                            return hostsLine ? (
                              <div className="event-hosts">{hostsLine}</div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      {event.imgKey && (
                        <div className="event-image">
                          <img
                            src={`https://s3.amazonaws.com/social-images-public/${event.imgKey}`}
                            alt={event.title}
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="event-details">
                        <div className="event-location">
                          <span className="location-pin">📍</span>{" "}
                          {/* The server only includes privateLocation for
                              entitled callers (approved / paid / female /
                              staff), so its presence IS the access check —
                              no client-side role test on top, which was
                              hiding it from approved regular users. */}
                          {event.privateLocation
                            ? event.privateLocation
                            : event.location}
                        </div>
                        {expandedEvents[event.id] && event.description && (
                          <div className="event-description">
                            <strong>Description:</strong> {event.description}
                          </div>
                        )}
                        {expandedEvents[event.id] && (
                          <div className="see-attendees">
                            <button
                              className="see-attendees-btn"
                              onClick={(e) => handleShowAttendees(e, event.id)}
                            >
                              See who's going
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Chats Tab */}
        {activeTab === "chats" && (
          <div className="tab-content">
            {userRole === "member" ||
            userRole === "admin" ||
            userInfo?.sex === "female" ? (
              <div className="chats-container">
                <h2>Group Chats</h2>
                {chats.length === 0 && !loading ? (
                  <div className="no-data">No chats available</div>
                ) : (
                  <div className="chats-list">
                    {chats.map((chat) => (
                      <div
                        key={chat.id}
                        className="chat-card"
                        onClick={() => handleChatSelect(chat.id)}
                      >
                        <div className="chat-avatar">
                          {chat.imageUrl ? (
                            <img src={chat.imageUrl} alt={chat.name} />
                          ) : (
                            <div className="chat-avatar-placeholder">
                              {chat.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="chat-details">
                          <h3>{chat.name}</h3>
                          {chat.description && (
                            <p className="chat-description">
                              {chat.description}
                            </p>
                          )}
                          <div className="chat-meta">
                            <span className="message-count">
                              {chat.messageCount || 0} messages
                            </span>
                            <span className="updated-at">
                              Last updated: {formatDate(chat.updatedAt * 1000)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="subscription-required">
                <div className="subscription-icon">💬</div>
                <h2>Join the Conversation</h2>
                <p>Subscribe to access group chats for all events</p>
                <p className="subscription-benefits">
                  With a subscription, you'll be able to:
                </p>
                <ul className="benefits-list">
                  <li>Chat with other event attendees</li>
                  <li>Get real-time updates from event organizers</li>
                  <li>See complete lists of who's attending</li>
                </ul>
                <button
                  className="subscribe-btn-large"
                  onClick={handleSubscribe}
                >
                  Subscribe Now
                </button>
              </div>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === "account" && (
          <div className="tab-content">
            <div className="account-section">
              <div className="user-profile">
                <h2>Account Settings</h2>
                {userInfo && (
                  <div className="account-details">
                    <ProfilePhotoControl
                      userId={userInfo.sub}
                      email={userInfo.email}
                      name={userInfo.name || userInfo.email}
                    />

                    <p className="account-email">
                      <strong>Email:</strong> {userInfo.email}
                    </p>
                    <p className="account-role">
                      <strong>Role:</strong> {userRole}
                    </p>

                    <MySubscriptionSection sex={userInfo?.sex || ""} />

                    <h3 className="profile-section-title">
                      Profile Information
                    </h3>
                    <div className="profile-info-container">
                      <div className="profile-info-item">
                        <strong>Phone:</strong>{" "}
                        {userInfo.phone || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Sex:</strong>{" "}
                        {userInfo.sex
                          ? userInfo.sex.charAt(0).toUpperCase() +
                            userInfo.sex.slice(1)
                          : "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>T-Shirt Size:</strong>{" "}
                        {userInfo.shirtSize || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Interests:</strong>{" "}
                        {userInfo.interestedEvents || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Referral Source:</strong>{" "}
                        {userInfo.referredBy || "Not provided"}
                      </div>
                    </div>

                    <div className="account-actions">
                      <button
                        className="edit-profile-btn"
                        onClick={() => {
                          setCurrentFeatureType("profile");
                          setShowUserInfoModal(true);
                        }}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </div>
                )}
                <button onClick={logoutFn} className="logout-btn">
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Prompt Modal */}
        {showSubscribePrompt && (
          <div className="modal-overlay" onClick={closeSubscribePrompt}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>See Who's Going</h3>
                <button className="close-button" onClick={closeSubscribePrompt}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  To see the attendee list, you need to subscribe to our premium
                  service.
                </p>
                <p>Get access to:</p>
                <ul>
                  <li>Complete attendee lists for all events</li>
                  <li>Access to group chats for each event</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button className="subscribe-btn" onClick={handleSubscribe}>
                  Subscribe Now
                </button>
                <button className="cancel-btn" onClick={closeSubscribePrompt}>
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Info Modal */}
        {showUserInfoModal && (
          <UserInfoModal
            onClose={() => setShowUserInfoModal(false)}
            onSubmit={handleUserInfoSubmit}
            email={userInfo.email}
            userInfo={userInfo}
            cognitoToken={cognitoToken}
            featureType={currentFeatureType}
          />
        )}
      </div>

      {/* Mobile App-like Bottom Navigation */}
      <div className="mobile-tab-navigation">
        <button
          className={activeTab === "events" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab("events");
          }}
        >
          <span className="tab-icon">📅</span>
          <span className="tab-label">Events</span>
        </button>
        <button
          className={activeTab === "chats" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            handleTabChange("chats");
          }}
        >
          <span className="tab-icon">💬</span>
          <span className="tab-label">Chats</span>
        </button>
        <button
          className={activeTab === "account" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab("account");
          }}
        >
          <span className="tab-icon">👤</span>
          <span className="tab-label">Account</span>
        </button>
      </div>
    </div>
  );
};

export default UserDashboard;
